import { useMemo } from 'react'
import * as React from 'react'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'

import LoadingBox from 'components/Common/Loading/LoadingBox'
import PropertyMap from 'components/Common/PropertyMap'
import type { PropertyMapOptions } from 'components/Common/PropertyMap/types'
import Group from 'components/utils/Group'
import { nonNullable } from 'lib/array/arrayUtils'
import { MAPBOX_ACCESS_TOKEN_USE_CASES } from 'lib/maps/mapboxAccessToken'

interface Props {
  primaryMapMarker?: App.MapMarker
  pickupMapMarker?: App.MapMarker
  locationMapMarkerList?: Array<App.MapMarker>
  style: React.CSSProperties
}

const MAP_OPTIONS: PropertyMapOptions = {
  fullscreenControl: false,
}
const MAP_LOAD_ROOT_MARGIN = '300px 0px'

const MapPlaceholder = styled(LoadingBox)`
  width: 100%;
  height: 100%;
`

function ExperienceMap(props: Props) {
  const {
    primaryMapMarker,
    pickupMapMarker,
    locationMapMarkerList,
    style,
  } = props

  const markers = useMemo(() => {
    return nonNullable([
      primaryMapMarker && {
        ...primaryMapMarker,
        openGoogleMapsOnSelect: false,
      },
      pickupMapMarker && {
        ...pickupMapMarker,
        openGoogleMapsOnSelect: false,
      },
      ...(locationMapMarkerList ?? []).map((marker) => ({
        ...marker,
        openGoogleMapsOnSelect: false,
      })),
    ])
  }, [locationMapMarkerList, pickupMapMarker, primaryMapMarker])
  const [mapInViewRef, hasMapBeenInView] = useInView({
    rootMargin: MAP_LOAD_ROOT_MARGIN,
    triggerOnce: true,
  })
  const mapContent = hasMapBeenInView ? (
    <PropertyMap
      height={style.height}
      wrapperStyle={style}
      markers={markers}
      multipleMarkers
      mapOptions={MAP_OPTIONS}
      showActiveMarkerState={false}
      mapboxAccessTokenUseCase={MAPBOX_ACCESS_TOKEN_USE_CASES.experiences}
    />
  ) : (
    <MapPlaceholder
      data-testid="experience-location-map-placeholder"
      borderRadius="S"
    />
  )

  return <Group direction="vertical">
    <div ref={mapInViewRef} style={style}>
      {mapContent}
    </div>
  </Group>
}

export default ExperienceMap
